import React, { Component, createRef } from "react";
import { Loader, Divider, Switch } from "@mantine/core";
import PropTypes from "prop-types";
import LexWorkplaceTooltip from "./LexWorkplaceTooltip";
import "./AsyncSwitch.css";
import { IconCheck, IconX } from "@tabler/icons-react";

export const SimpleSwitch = ({
	switchValue,
	handleSwitchValueChange,
	label,
	disabled,
	isValueUpdating,
	valueUpdatedSuccess,
	valueUpdatedFailed,
	disabledHoverText,
	labelVariant = "header-4",
	variant,
}) => {
	const switchRef = React.useRef(null);
	const getWrapperStyle = () => {
		if (variant === "formInput") {
			return {
				border: "1px solid #ccc",
				borderRadius: 4,
				borderLeft: "8px solid",
				borderLeftColor: "var(--lxw-main-500)",
				padding: "8px",
				marginBottom: 12,
				minHeight: 38,
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				backgroundColor: disabled ? "#f5f5f5" : "white",
				cursor: disabled ? "not-allowed" : "pointer",
			};
		}
		return {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
		};
	};

	const getLabel = () => {
		if (labelVariant === "header-4") {
			return <h4>{label}</h4>;
		}
		if (labelVariant === "header-5") {
			return <h5>{label}</h5>;
		}
		return <div>{label}</div>;
	};

	return (
		<LexWorkplaceTooltip disabledHoverText={disabledHoverText}>
			<div
				style={getWrapperStyle()}
				onClick={
					variant === "formInput"
						? (e) => {
								if (switchRef.current?.contains(e.target))
									return;
								handleSwitchValueChange();
							}
						: undefined
				}
			>
				{getLabel()}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 5,
					}}
				>
					<div>
						{isValueUpdating ? <Loader size={15} /> : ""}
						{valueUpdatedSuccess ? (
							<IconCheck
								size={16}
								color="green"
								style={{ verticalAlign: "middle" }}
							/>
						) : (
							""
						)}
						{valueUpdatedFailed ? (
							<IconX
								size={16}
								color="red"
								style={{ verticalAlign: "middle" }}
							/>
						) : (
							""
						)}
					</div>
					<div ref={switchRef}>
						<Switch
							size="xs"
							checked={switchValue}
							onChange={handleSwitchValueChange}
							disabled={disabled || isValueUpdating}
							data-testid={label}
						/>
					</div>
				</div>
			</div>
		</LexWorkplaceTooltip>
	);
};
class AsyncSwitch extends Component {
	constructor(props) {
		super(props);
		this.state = {
			canSubmit: false,
			switchValue: false,
			switchUpdating: false,
			updateSuccess: false,
			updateFailed: false,
			loading: true,
		};
		this.requestIdRef = createRef();
		this.requestIdRef.current = 0;
		this.timeoutRefs = {
			stateUpdate: createRef(),
			statusClear: createRef(),
		};
		this.handleStateUpdate = this.handleStateUpdate.bind(this);
	}

	static getDerivedStateFromProps(props, state) {
		if (state.loading && !props.loading) {
			const newState = {
				loading: false,
				switchValue: !!props.switchValue,
			};
			return newState;
		} else {
			return null;
		}
	}

	componentDidMount() {
		this._isMounted = true;
	}

	componentWillUnmount() {
		this._isMounted = false;
		// Clean up any pending timeouts
		if (this.timeoutRefs.stateUpdate.current) {
			clearTimeout(this.timeoutRefs.stateUpdate.current);
		}
		if (this.timeoutRefs.statusClear.current) {
			clearTimeout(this.timeoutRefs.statusClear.current);
		}
	}

	handleStateUpdate(value, success, failed) {
		// Clear any existing timeout
		if (this.timeoutRefs.stateUpdate.current) {
			clearTimeout(this.timeoutRefs.stateUpdate.current);
		}

		this.timeoutRefs.stateUpdate.current = setTimeout(() => {
			if (this._isMounted) {
				this.setState(
					{
						switchValue: value,
						switchUpdating: false,
						updateSuccess: success,
						updateFailed: failed,
					},
					() => {
						if (this._isMounted) {
							// Clear any existing status clear timeout
							if (this.timeoutRefs.statusClear.current) {
								clearTimeout(
									this.timeoutRefs.statusClear.current
								);
							}

							this.timeoutRefs.statusClear.current = setTimeout(
								() => {
									if (this._isMounted) {
										this.setState({
											updateSuccess: false,
											updateFailed: false,
										});
									}
								},
								1000
							);
						}
					}
				);
			}
		}, 1000);
	}

	render() {
		return (
			<>
				<SimpleSwitch
					label={this.props.label}
					switchValue={this.state.switchValue}
					isValueUpdating={this.state.switchUpdating}
					valueUpdatedSuccess={this.state.updateSuccess}
					valueUpdatedFailed={this.state.updateFailed}
					disabledHoverText={this.props.disabledHoverText}
					labelVariant={this.props.labelVariant}
					variant={this.props.variant}
					handleSwitchValueChange={() => {
						const newSwitchValue = !this.state.switchValue;
						const requestId = ++this.requestIdRef.current;

						this.setState(
							{
								switchValue: newSwitchValue,
								switchUpdating: true,
							},
							() => {
								const result =
									this.props.updateSetting(newSwitchValue);
								if (
									result &&
									typeof result.then === "function"
								) {
									result
										.then(() => {
											if (
												requestId ===
												this.requestIdRef.current
											) {
												this.handleStateUpdate(
													newSwitchValue,
													true,
													false
												);
											}
										})
										.catch(() => {
											if (
												requestId ===
												this.requestIdRef.current
											) {
												this.handleStateUpdate(
													!newSwitchValue,
													false,
													true
												);
											}
										});
								} else {
									this.handleStateUpdate(
										newSwitchValue,
										true,
										false
									);
								}
							}
						);
					}}
					labelPlacement="start"
					disabled={this.props.disabled}
				/>
				<Divider />
			</>
		);
	}
}
AsyncSwitch.propTypes = {
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	switchValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
	disabled: PropTypes.bool.isRequired,
	labelVariant: PropTypes.oneOf(["header-4", "header-5", "div"]),
	variant: PropTypes.oneOf(["formInput"]),
};

export const LabeledSwitch = ({
	upperLabel,
	label,
	switchValue,
	handleSwitchValueChange,
	disabled = false,
}) => {
	return (
		<div className="LabeledInput">
			<div className="InputLabel">{upperLabel || "\u00A0"}</div>
			<SimpleSwitch
				label={label}
				switchValue={switchValue}
				handleSwitchValueChange={handleSwitchValueChange}
				disabled={disabled}
				variant="formInput"
				labelVariant="div"
			/>
		</div>
	);
};

LabeledSwitch.propTypes = {
	upperLabel: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	switchValue: PropTypes.bool.isRequired,
	handleSwitchValueChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default AsyncSwitch;
